const FableServiceProviderBase = require('fable-serviceproviderbase');
//const SyncShim = require('sync-shim');

/** @typedef {import('fable')} Fable */
/** @typedef {import('headlight-rest-client')} HeadlightRestClient */

/** How long after the render the page may use the document data the render put in it. */
const INLINE_REPORT_PARAMETERS_MAX_AGE_MS = 60000;

/**
 * Document management services (load, save, download, etc.).
 */
class HeadlightDocumentService extends FableServiceProviderBase
{
	/**
	 * @param {Fable} pFable - The fable instance
	 * @param {any} pOptions - Options for this service
	 * @param {any} pServiceHash - The fable service hash for this instance
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('fable') & { PictApplication?: any, AppData?: any, MetaTemplate?: any }} */
		this.pict = this.fable; // for parity with pict providers / views

		this.serviceType = 'HeadlightDocumentService';
	}

	/**
	 * @return {HeadlightRestClient} the headlight client instance
	 */
	get HeadlightRestClient()
	{
		return this.fable.services.HeadlightRestClient;
	}

	/**
	 * Get the current document id from the URL or the DOM
	 *
	 * @return {string} The current document id
	 */
	get documentId()
	{
		if (!this._documentId)
		{
			let documentId = null;
			const currentDocIDElement = document.querySelector('#Data_IDDocument');
			if (currentDocIDElement)
			{
				documentId = currentDocIDElement.textContent.trim();
			}

			if (!documentId)
			{
				const params = new URLSearchParams(window.location.search);
				documentId = params.get('documentID') || params.get('IDDocument');
			}

			this._documentId = documentId;
		}

		return this._documentId;
	}

	/**
	 * The document data the render put in the page (`<script id="Data_ReportParameters">`), so the form can skip
	 * fetching `Document/:id/ReportParameters`, which builds the same object again. Used only on a fresh open:
	 * the page URL keeps working after the open, and booting a refresh or a reopened tab from the render-time copy
	 * could save stale data over newer edits.
	 *
	 * @param {string|number} pDocumentID - The page's document id.
	 * @return {?any} The same object `ReportParameters` returns, or null to fetch it instead.
	 */
	readInlineReportParameters(pDocumentID)
	{
		const tmpDecline = (pReason) =>
		{
			this.log.info(`Fetching ReportParameters: ${pReason}`);
			return null;
		};
		if ((typeof document === 'undefined') || (typeof window === 'undefined'))
		{
			return null;
		}
		const tmpElement = document.getElementById('Data_ReportParameters');
		if (!tmpElement)
		{
			return null;
		}
		const tmpText = tmpElement.textContent;
		tmpElement.remove();

		const tmpParams = new URLSearchParams(window.location.search);
		let tmpForceFetch = tmpParams.get('fetchReportParameters') === '1';
		try
		{
			tmpForceFetch = tmpForceFetch || (window.localStorage.getItem('HeadlightFetchReportParameters') === '1');
		}
		catch (pError)
		{
			// Storage can be unavailable (privacy modes, sandboxed frames); the URL switch still works.
		}
		if (tmpForceFetch)
		{
			return tmpDecline('forced by fetchReportParameters');
		}

		const tmpNavigation = (typeof performance !== 'undefined') && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
		if (!tmpNavigation || (tmpNavigation.type !== 'navigate'))
		{
			return tmpDecline(`the page was not freshly opened (${tmpNavigation ? tmpNavigation.type : 'no navigation timing'})`);
		}

		const tmpSessionToken = tmpParams.get('SessionToken');
		if (!tmpSessionToken)
		{
			return tmpDecline('the page has no session token of its own');
		}

		let tmpReportParameters;
		try
		{
			tmpReportParameters = JSON.parse(tmpText);
		}
		catch (pError)
		{
			return tmpDecline(`the inline copy did not parse: ${pError.message}`);
		}
		if (!tmpReportParameters || !tmpReportParameters.Document || (Number(tmpReportParameters.Document.IDDocument) !== Number(pDocumentID)))
		{
			return tmpDecline('the inline copy is for another document');
		}

		const tmpServerNow = this.HeadlightRestClient && this.HeadlightRestClient.estimateServerTime ? this.HeadlightRestClient.estimateServerTime() : null;
		const tmpRenderedAt = Date.parse(tmpReportParameters.RenderedAt);
		if ((tmpServerNow === null) || Number.isNaN(tmpRenderedAt))
		{
			return tmpDecline('no server time to judge its age');
		}
		// Absolute: the render stamp and the server time come from different containers' clocks.
		const tmpAgeMS = tmpServerNow - tmpRenderedAt;
		if (Math.abs(tmpAgeMS) > INLINE_REPORT_PARAMETERS_MAX_AGE_MS)
		{
			return tmpDecline(`the inline copy is ${Math.round(tmpAgeMS / 1000)} s old`);
		}

		delete tmpReportParameters.RenderedAt;
		tmpReportParameters.SessionToken = tmpSessionToken;
		this.log.info(`Using the document data the render put in the page (${Math.round(tmpAgeMS / 1000)} s old)`);
		return tmpReportParameters;
	}

	/**
	 * Fetch the current document from the server
	 *
	 * @return {Promise<any>} The current document
	 */
	async fetchCurrentDocumentReportParameters()
	{
		if (this.pict.settings.IsHeadlightWebApplication)
		{
			// Headlight Document service skipping download Document entity record because this app has flagged itself as a web application
			return false;
		}
		const documentId = this.documentId;
		if (documentId)
		{
			if (Number.isNaN(documentId))
			{
				this.log.error(`Invalid document id ${documentId}`);
				return null;
			}
			const tmpInline = this.readInlineReportParameters(documentId);
			this.reportParametersSource = tmpInline ? 'inline' : 'fetched';
			const doc = tmpInline || await this.fetchDocumentReportParameters(documentId);
			this.pict.PictApplication.AppData.DocumentData = doc;
			return doc;
		}
		else
		{
			this.log.error('Failed to fetch current document, no document id provided');
		}
	}

	/**
	 * Read a document's report parameters from the server without adopting them, so a caller can compare
	 * them with what the page already holds.
	 *
	 * @param {string|number} pDocumentID - The document to read.
	 * @return {Promise<any>} The `Document/:id/ReportParameters` body.
	 */
	fetchDocumentReportParameters(pDocumentID)
	{
		return new Promise((resolve, reject) =>
		{
			this.HeadlightRestClient.getJSON(`Document/${pDocumentID}/ReportParameters`, (err, body) =>
			{
				if (err)
				{
					return reject(err);
				}
				resolve(body);
			});
		});
	}

	/**
	 * The native document bridge this form persists through, or null when it persists to the server.
	 *
	 * On the iOS offline template the native layer owns the document row: the form writes to local
	 * storage and the device syncs the record up later. The record it writes is the same one the PUT
	 * would carry, so this is a transport choice and nothing above it needs to know which one ran.
	 *
	 * @return {?any} The bridge, or null.
	 */
	get localDocumentBridge()
	{
		if (typeof window === 'undefined')
		{
			return null;
		}
		/** @type {any} */
		const tmpWindow = window;
		const tmpBridge = tmpWindow._configurableFormDocumentBridge;
		if (!tmpWindow._OfflineMode || !tmpBridge || typeof tmpBridge.saveDocument !== 'function')
		{
			return null;
		}
		return tmpBridge;
	}

	/**
	 * Whether a save from this form lands locally rather than on the server.
	 *
	 * Load-bearing for `Document.EntityData`: the server applies the comprehension when the record
	 * reaches it, so anything that may only advance once the server has seen the payload has to ask
	 * this first.
	 *
	 * @return {boolean} True when the save is written to local storage.
	 */
	isLocalDocumentTransport()
	{
		return !!this.localDocumentBridge;
	}

	/**
	 * Build the document record a save writes, whichever transport carries it.
	 *
	 * @return {Record<string, any>} The record to persist.
	 */
	buildDocumentSaveRecord()
	{
		const tmpDocument = this.pict.PictApplication.AppData.DocumentData.ReportData;
		// spread the IDObservation list into a new array so we don't mutate the original collection
		const tmpObservations = Array.isArray(tmpDocument.Observations) ? tmpDocument.Observations : [];
		return {
			IDDocument: tmpDocument.IDDocument,
			IDProject: tmpDocument.IDProject,
			FormData: tmpDocument.FormData,
			EntityData: tmpDocument.EntityData,
			Observations: [ ...tmpObservations.map((obs) => obs.IDObservation) ],
		};
	}

	/**
	 * Do the document save (this is the same as clicking the save button in the UI).
	 *
	 * Resolves with the saved record the transport handed back, which the server decorates and the
	 * local bridge does not — callers must treat everything on it as optional.
	 *
	 * @return {Promise<any>}
	 * @memberof HeadlightDocumentService
	 * @async
	 */
	async saveDocument()
	{
		try
		{
			const tmpRecord = this.buildDocumentSaveRecord();
			const tmpLocalBridge = this.localDocumentBridge;
			if (tmpLocalBridge)
			{
				return await this._saveDocumentToLocalBridge(tmpLocalBridge, tmpRecord);
			}
			const response = await new Promise((resolve, reject) =>
			{
				this.HeadlightRestClient.putJSON(`Document/`, tmpRecord,
				(err, body) =>
				{
					if (err)
					{
						return reject(err);
					}
					resolve(body);
				});
			});
			return response;
		}
		catch (error)
		{
			this.log.error('Failed to save document', { error: error.message, stack: error.stack });
			throw error;
		}
	}

	/**
	 * Hand the document record to the native bridge and wait for it to be persisted.
	 *
	 * Rejects on a bridge failure so the save lifecycle reports it exactly as it reports a failed PUT;
	 * a write that did not land must not be reported as one that did.
	 *
	 * @param {any} pBridge - The native document bridge.
	 * @param {Record<string, any>} pRecord - The record to persist.
	 * @return {Promise<any>} Resolves with whatever the bridge returned.
	 */
	_saveDocumentToLocalBridge(pBridge, pRecord)
	{
		return new Promise((resolve, reject) =>
		{
			try
			{
				pBridge.saveDocument(pRecord, (pError, pResult) =>
				{
					if (pError)
					{
						return reject((pError instanceof Error) ? pError : new Error(String(pError)));
					}
					return resolve(pResult);
				});
			}
			catch (pError)
			{
				return reject((pError instanceof Error) ? pError : new Error(String(pError)));
			}
		});
	}

	/**
	 * Downloads the document as a PDF file; if the document is not locked, then a refreshed PDF is generated
	 * @param {boolean} forceRender - If true, then a refreshed PDF is generated regardless of the document's locked state
	 * @param {object} [pReportData] - Optional alternative ReportData (if we want to download "not my current document" data)
	 * @param {function} [pCallback] - Optional callback when the download is complete
	 * @return {Promise<void>}
	 */
	async downloadDocument(forceRender = false, pReportData, pCallback)
	{
		const reportData = pReportData || this.pict.PictApplication.AppData.DocumentData.ReportData;
		let url = `/1.0/Document/${reportData.IDDocument}/DCR.pdf`;
		// if the document is not locked, then we need to regenerate a rerfeshed pdf
		if (forceRender || !reportData.Locked)
		{
			// fetch the GUIDReportDescription from the server to use in the url
			const response = await new Promise((resolve, reject) =>
			{
				this.HeadlightRestClient.getJSON(`Report/Render/${reportData.IDDocument}/pdf`, (err, body) =>
				{
					if (err)
					{
						reject(err);
					}
					else
					{
						resolve(body);
					}
				});
			});
			url = `/1.0/Report/${ response.GUIDReportDescription }/index.pdf`;
		}
		// format the metadata for the pdf file name
		const metaList = [
			reportData.DocumentType,
			this.fable.Dates.dayJS(reportData.DocumentDate).format('YYYY-MM-DD'),
		];
		// create a download link and click it to download the pdf
		const downloadLink = document.createElement('a');
		downloadLink.href = url;
		downloadLink.classList.add('is-hidden');
		// config option to optionally show the project name in the pdf download file name
		if (reportData.ReportOptions?.Meta?.ShowProjectNameInPDF)
		{
			metaList.push(reportData?.Project?.Name?.replace(/[^a-z0-9]/gi, '_'));
		}
		downloadLink.download = metaList.join('_') + '.pdf';
		document.body.appendChild(downloadLink);
		downloadLink.click();
		document.body.removeChild(downloadLink);

		if (pCallback && typeof pCallback === 'function')
		{
			pCallback();
		}
	}

	/**
	 * Get the current document's approval state
	 * @return {Promise<any>} The approval state of the document
	 * @memberof HeadlightDocumentService
	 * @async
	 */
	async fetchApprovalState()
	{
		try
		{
			const docId = this.pict.PictApplication.AppData?.DocumentData?.ReportData?.IDDocument;
			if (!docId)
			{
				this.log.error('No document id found');
				return null;
			}
			const approvalResults = await new Promise((resolve, reject) =>
			{
				this.HeadlightRestClient.getJSON(`DocumentApproval/${docId}/State`, (err, body) =>
				{
					if (err)
					{
						reject(err);
						return;
					}
					resolve(body);
				});
			});
			this.log.info('Approval state fetched', { approvalResults });
			return approvalResults;
		}
		catch (error)
		{
			this.log.error('Failed to get approval state', { error: error.message, stack: error.stack });
			throw error;
		}
	}

	/**
	 * Fetch the list of users that can approve the current document
	 * @return {Promise<any>} The list of users that can approve the document
	 * @memberof HeadlightDocumentService
	 * @async
	 */
	async fetchApprovalUsersList()
	{
		try
		{
			const docId = this.pict.PictApplication.AppData?.DocumentData?.ReportData?.IDDocument;
			if (!docId)
			{
				this.log.error('No document id found');
				return null;
			}
			const approvalUserListResults = await new Promise((resolve, reject) =>
			{
				this.HeadlightRestClient.getJSON(`DocumentApproval/${docId}/ApproverList`, (err, body) =>
				{
					if (err)
					{
						reject(err);
						return;
					}
					resolve(body);
				});
			});
			this.log.info('Approval user list fetched', { approvalUserListResults });
			return approvalUserListResults;
		}
		catch (error)
		{
			this.log.error('Failed to get approval user list', { error: error.message, stack: error.stack });
			throw error;
		}
	}

	/**
	 * Fetch the approval history of the current document (logs of who approved/rejected the document and when)
	 * @return {Promise<any>} The approval history of the document
	 * @memberof HeadlightDocumentService
	 * @async
	 */
	async fetchApprovalHistory()
	{
		try
		{
			const docId = this.pict.PictApplication.AppData?.DocumentData?.ReportData?.IDDocument;
			if (!docId)
			{
				this.fable.log.error('No document id found');
				return null;
			}
			const approvalHistoryResults = await new Promise((resolve, reject) =>
			{
				this.HeadlightRestClient.getJSON(`DocumentApprovalLogs/By/IDDocument/${docId}`, (err, body) =>
				{
					if (err)
					{
						reject(err);
						return;
					}
					resolve(body);
				});
			});
			this.fable.log.info('Approval history fetched', { approvalHistoryResults });
			return approvalHistoryResults;
		}
		catch (error)
		{
			this.fable.log.error('Failed to get approval history', error);
			throw error;
		}
	}

	/**
	 * Take an approval action on the current document (approve, reject, etc)
	 * @param {string} pAction The action to take on the document
	 * @return {Promise<any>} The results of the approval action
	 * @memberof HeadlightDocumentService
	 * @async
	 * @example
	 * await this.fable.services.HeadlightDocumentService.doApprovalAction('Approve');
	 * await this.fable.services.HeadlightDocumentService.doApprovalAction('Reject');
	 */
	async doApprovalAction(pAction)
	{
		try
		{
			const docId = this.pict.PictApplication.AppData?.DocumentData?.ReportData?.IDDocument;
			if (!docId)
			{
				this.fable.log.error('No document id found');
				return null;
			}
			const approvalResults = await new Promise((resolve, reject) =>
			{
				this.HeadlightRestClient.postJSON(`DocumentApproval`,
				{
					Action: pAction,
					IDDocument: docId,
				},
				(err, body) =>
				{
					if (err)
					{
						reject(err);
						return;
					}
					resolve(body);
				});
			});
			this.fable.log.info('Approval action taken', { approvalResults });
			return approvalResults;
		}
		catch (error)
		{
			this.fable.log.error('Failed to take approval action', error);
			throw error;
		}
	}

	// TODO: Move this to a User provider? It doesn't belong here, but not sure where yet.
	/**
	 * Update the user's settings on the server.
	 *
	 * Impersonated sessions write somewhere else, and write less. `AppData.CurrentUser` is the
	 * *impersonated* user — including their Settings — so persisting it as-is would save a tester's
	 * preference onto the customer's record. Retargeting the write at the real user is not enough on
	 * its own either: sending CurrentUser.Settings to the real user would overwrite the real user's
	 * preferences with the impersonated user's. So an impersonated write goes to the real user
	 * (`ImpersonatingIDUser`, which stays pointed at the root user across chained impersonations) and
	 * carries only the keys that actually changed, merged onto that user's stored settings.
	 *
	 * Note the read side is unchanged: while impersonating, the panels still read the impersonated
	 * user's preferences, which is what makes "see what they see" work. A preference changed while
	 * impersonating therefore applies to the tester's own next session, not the current one.
	 *
	 * @param {Object} [pChangedSettings] - The settings that changed, e.g. `{ HeadlightPanels: { AutoSave: false } }`.
	 *   Required for impersonated sessions; without it there is no safe way to write.
	 * @memberof HeadlightDocumentService
	 * @async
	 */
	async updateUserSettings(pChangedSettings)
	{
		try
		{
			const user = this.pict.AppData.CurrentUser;
			if (!user)
			{
				this.log.warn('No current user; not updating user settings.');
				return;
			}
			if (user.Impersonation)
			{
				return await this._updateImpersonatingUserSettings(user, pChangedSettings);
			}
			this.pict.log.info('Updating user settings', { user });

			const userResults = await new Promise((resolve, reject) =>
			{
					this.HeadlightRestClient.putJSON(`User`,
					{
						IDUser: user.UserID,
						Settings: user.Settings,
					},
					(err, body) =>
					{
						if (err)
						{
							reject(err);
							return;
						}
						resolve(body);
					});
			});
				this.fable.log.info('User Settings updated', { userResults });
				return userResults;
		}
		catch (error)
		{
			this.log.error('Failed to update user settings', { error: error.message, stack: error.stack });
			throw error;
		}
	}

	/**
	 * Persist a settings change made during an impersonated session onto the real user's record.
	 *
	 * Refuses rather than guesses: with no changed-settings delta, or no real user to write to, the
	 * only options are to overwrite the real user's preferences with the impersonated user's or to
	 * write the tester's preference onto the customer's record. Skipping beats both.
	 *
	 * @param {any} pCurrentUser - The impersonated session user.
	 * @param {Object} [pChangedSettings] - The settings that changed.
	 * @return {Promise<any>}
	 */
	async _updateImpersonatingUserSettings(pCurrentUser, pChangedSettings)
	{
		const tmpRealIDUser = pCurrentUser.ImpersonatingIDUser;
		if (!tmpRealIDUser)
		{
			this.log.warn('Impersonated session carries no ImpersonatingIDUser; not saving the settings change to either user.');
			return;
		}
		if (!pChangedSettings || typeof pChangedSettings !== 'object')
		{
			this.log.warn(`Impersonated session changed a setting without saying which; not saving it to user ${tmpRealIDUser}.`);
			return;
		}
		const tmpRealUser = await new Promise((resolve) =>
		{
			this.HeadlightRestClient.getJSON(`User/${tmpRealIDUser}`, (pError, pBody) =>
			{
				if (pError)
				{
					this.log.error(`Failed to load the impersonating user ${tmpRealIDUser} to save a settings change: ${pError.message || pError}`);
					return resolve(null);
				}
				return resolve(pBody);
			});
		});
		if (!tmpRealUser)
		{
			return;
		}
		let tmpRealSettings = tmpRealUser.Settings;
		if (!tmpRealSettings && tmpRealUser.SettingsJSON)
		{
			try
			{
				tmpRealSettings = JSON.parse(tmpRealUser.SettingsJSON);
			}
			catch (pParseError)
			{
				this.log.warn(`Could not parse settings for user ${tmpRealIDUser}; starting from an empty set.`);
			}
		}
		tmpRealSettings = tmpRealSettings || { };
		// Shallow per-namespace merge: the delta names a namespace (HeadlightPanels) and the keys within
		// it that changed, so the real user's other preferences in that namespace survive.
		for (const [ tmpNamespace, tmpValues ] of Object.entries(pChangedSettings))
		{
			if (tmpValues && typeof tmpValues === 'object' && !Array.isArray(tmpValues))
			{
				tmpRealSettings[tmpNamespace] = Object.assign({ }, tmpRealSettings[tmpNamespace], tmpValues);
			}
			else
			{
				tmpRealSettings[tmpNamespace] = tmpValues;
			}
		}
		this.log.info(`Impersonated session: saving the settings change to the real user ${tmpRealIDUser}, not the impersonated user ${pCurrentUser.UserID}.`, { ChangedSettings: pChangedSettings });
		return await new Promise((resolve, reject) =>
		{
			this.HeadlightRestClient.putJSON(`User`, { IDUser: tmpRealIDUser, Settings: tmpRealSettings }, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				return resolve(pBody);
			});
		});
	}
}

module.exports = HeadlightDocumentService;
